import {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  forwardRef,
} from 'react'
import { logger } from '../../services/logger'
import useApi from '../../api/apiProvider'
import {
  calculateBatchProductsPricing,
  getDiscounts,
  getPricingItems,
} from './Pricing.api'
import { promoCodeManager } from '../../services/PromoCodeManager'
import useOnMount from '../../hooks/useOnMount'
import { useProductType } from '../../services/ProductTypesManager'
import {
  getRelevantVariants,
  PRICING_ERROR_CONTEXT,
  isSinglePriceSingleSize,
} from './PricingUtil'
import { currencyState } from '../../services/CurrencyState'
import debounce from 'lodash/debounce'

import { getDiscountForSize } from './Discounts/Discounts'
import {
  fallbackUntilMatch,
  FRAME_COLORS,
  hashTilePricingProduct,
  isCenterpiece,
  MATERIAL_TYPES,
  type PricingProduct,
  TILE_SIZES,
} from '@mixtiles/web-backend-shared'
import { runAsyncWithRetries } from '../../utils/promises'
import useDialog from '../../elements/Dialog/DialogProvider'
import { translateManager as t } from '../../services/TranslateManager'
import { sendPricingErrorAnalytics } from '../../services/Analytics/pricingAnalytics'
import {
  MIXED_MATERIALS,
  MIXED_SIZES,
} from '../../pages/PhotoStyler/TileDesignerConsts'
import { getOrderedSizes } from '../../pages/DiscoveryPage/DiscoveryConsts'
import {
  type BatchProductsPricingResponse,
  type ProductCollection,
} from './types/checkout.types'
import { type PricingItem } from './types/pricingItem.types'
import {
  type DiscountsAPI,
  type FetchDiscountsResponse,
} from './types/discount.types'
import {
  type DiscountData,
  type FetchDiscountParams,
  type FetchDiscountResponse,
  type FetchDiscountsForPromoCodeParams,
  type FetchDiscountsForPromoCodeResponse,
  type FetchPricingItemResponse,
  type FetchPricingResponse,
  type PriceDifferenceMap,
  type PricingContextValue,
  type PricingData,
} from './types/PricingProvider.types'
import { PRODUCT_TYPES } from '../../services/ProductTypeState'
import { useEvent } from 'react-use-event-hook'
import {
  getFrameColorForPricing,
  getActualMaterialsInUse,
  getPricingMaterialType,
} from '../../utils/materialTypeUtils'
import { useExperimentManager } from '../../services/ExperimentManager/ExperimentManager'
import { useKeys } from 'services/KeysProvider'
import {
  FETCH_PRICING_ITEMS_OPERATION_NAME,
  RELOAD_DISCOUNTS_OPERATION_NAME,
  REFRESH_PRICING_NUM_RETRIES,
  DEBOUNCE_INTERVAL,
} from '../../stores/pricingStore/pricing.consts'
import {
  formatPricingItems,
  getDiscountProductTypes,
  getPricingProductType,
  shouldUseAPI,
} from '../../stores/pricingStore/pricing.utils'
import { isClient } from 'utils/runtimeUtils'
import { formatPrice } from 'utils/utils'

const PricingContext = createContext<PricingContextValue | null>(null)
const frameColors = Object.values(FRAME_COLORS)
const materialTypes = Object.values(MATERIAL_TYPES)

export function usePricing() {
  const context = useContext(PricingContext)
  if (!context) {
    throw new Error('usePricing must be within PricingContextProvider')
  }
  return context
}

export function withPricing(Component: any) {
  return forwardRef((props, ref) => (
    <PricingContext.Consumer>
      {(contexts) => <Component {...props} ref={ref} pricing={contexts} />}
    </PricingContext.Consumer>
  ))
}

export function PricingProvider({
  initialPricingData,
  children,
}: PropsWithChildren & { initialPricingData: PricingData }) {
  const isFirstInit = useRef(true)
  const { ipCountry } = useKeys()
  const api = useApi()
  const dialog = useDialog()
  const { productType } = useProductType()
  const [discounts, setDiscounts] = useState<DiscountData>({
    value: [],
    isLoading: true,
  })
  const [pricingData, setPricingData] = useState<PricingData>(() => {
    const clonedPricingData = { ...initialPricingData }
    clonedPricingData.pricingItems = formatPricingItems(
      initialPricingData.pricingItems as unknown as never
    )
    return clonedPricingData
  })
  useEffect(() => {
    currencyState.setCurrency(initialPricingData.currency)
    promoCodeManager.revalidateDiscountCouponsForCurrency(
      initialPricingData.currency
    )
  }, [initialPricingData.currency])

  const experimentManager = useExperimentManager()

  const [error, setError] = useState<any>(null)
  const [pricingVariantsIds, setPricingVariantsIds] = useState<string[]>(() =>
    getRelevantVariants(experimentManager)
  )
  const pricingCountry = ipCountry
  const lastPromocode = useRef(
    isClient() ? promoCodeManager.getDiscountCouponDisplayName() : null
  )

  const [priceDifferenceMap, setPriceDifferenceMap] =
    useState<PriceDifferenceMap>(null)

  const setDataForAll = (
    pricingItems: PricingItem[],
    discountValue: DiscountsAPI,
    currency: string,
    pricingProductType: PRODUCT_TYPES
  ) => {
    const formattedPricingItems = formatPricingItems(pricingItems)

    setPricingData({
      currency,
      pricingItems: formattedPricingItems,
      productType: pricingProductType,
      isLoading: false,
    })
    setDiscounts({
      value: discountValue,
      isLoading: false,
    })
  }

  const setDiscountsIsLoading = (isLoading: boolean) => {
    setDiscounts({ value: discounts.value, isLoading })
  }

  const reportPricingError = (
    message: string,
    error: any,
    source: string,
    couponName?: string
  ) => {
    sendPricingErrorAnalytics(error, source, message)
    logger.error(message, error, {
      tags: { context: PRICING_ERROR_CONTEXT },
      pricingCountry,
      pricingVariantsIds,
      productType,
      couponName,
    })
  }

  const refreshPage = () => {
    window.location.reload()
  }

  const handleError = (message: string, error: any, source: string) => {
    reportPricingError(message, error, source)
    setError(error)
    dialog.showAlert(
      t.get('general.errors.general_title'),
      t.get('general.errors.general_description'),
      t.get('general.refresh'),
      refreshPage
    )
  }

  const setIsLoading = (isLoading: boolean) => {
    setDiscountsIsLoading(isLoading)
    setPricingData({ ...pricingData, isLoading })
  }

  const fetchDiscounts = async (
    forceCouponValidation?: boolean
  ): Promise<FetchDiscountsResponse> => {
    await promoCodeManager.revalidateDiscountCoupon(forceCouponValidation)
    const promocode = promoCodeManager.getDiscountCouponCode()
    return api.call(
      getDiscounts({
        productType: getDiscountProductTypes(productType),
        pricingCountry,
        variantsIds: pricingVariantsIds,
        promocode,
      })
    )
  }

  const fetchDiscount = useCallback(
    ({
      tileSize,
      materialType = MATERIAL_TYPES.CLASSIC,
      frameColor,
      tiles,
    }: FetchDiscountParams): FetchDiscountResponse => {
      if (!discounts.isLoading && !pricingData.isLoading) {
        const nonCenterpieceTiles =
          tiles?.filter((tile) => !isCenterpiece(tile)) ?? []

        const actualMaterialsInUse = getActualMaterialsInUse(
          nonCenterpieceTiles
        )?.length
          ? getActualMaterialsInUse(nonCenterpieceTiles)
          : [
              {
                material: MATERIAL_TYPES.CLASSIC,
                frameColor: FRAME_COLORS.BLACK,
              },
            ]

        // Photo Books don't have tiles/materials - skip the mixed-size logic
        const isSinglePriceSingleSizeResult =
          productType === PRODUCT_TYPES.PHOTO_BOOK
            ? true
            : isSinglePriceSingleSize({
                tileSize,
                actualMaterialsInUse,
                pricingItems: pricingData.pricingItems,
              })

        // Pass actualMaterialsInUse to getPricingMaterialType only when prices are same
        // When prices differ, pass undefined so it defaults to WIDE_FRAME
        const backendMaterialType = getPricingMaterialType(
          materialType,
          isSinglePriceSingleSizeResult ? actualMaterialsInUse : undefined
        )

        const actualFrameColor = getFrameColorForPricing({
          backendMaterialType,
          isSinglePriceSingleSize: isSinglePriceSingleSizeResult,
          frameColor,
          tiles: nonCenterpieceTiles,
        })

        return {
          value: getDiscountForSize({
            discounts: discounts.value,
            tileSize: isSinglePriceSingleSizeResult ? tileSize : MIXED_SIZES,
            currency: pricingData.currency,
            materialType: backendMaterialType,
            frameColor: actualFrameColor,
          }),
          isLoading: false,
        }
      } else {
        return { value: null, isLoading: true }
      }
    },
    [pricingData, discounts, productType]
  )

  const fetchDiscountsForPromoCode = async ({
    promocode,
    tileSize,
    materialType = MATERIAL_TYPES.CLASSIC,
    frameColor,
    forceProductType = null,
  }: FetchDiscountsForPromoCodeParams): FetchDiscountsForPromoCodeResponse => {
    const backendMaterialType =
      materialType === MATERIAL_TYPES.FRAMELESS
        ? MATERIAL_TYPES.CLASSIC
        : materialType
    const { discounts } = await api.call(
      getDiscounts({
        productType: [forceProductType || getPricingProductType(productType)],
        pricingCountry,
        variantsIds: pricingVariantsIds,
        promocode,
      })
    )
    return {
      value: getDiscountForSize({
        discounts,
        tileSize,
        currency: pricingData.currency,
        materialType: backendMaterialType,
        frameColor,
      }),
      isLoading: false,
    }
  }

  const fetchPricing = async (): Promise<FetchPricingResponse> => {
    logger.info(
      `[PricingProvider] - Starting pricing fetch - productType: ${productType} ${pricingCountry} ${pricingVariantsIds}`,
      {
        tags: { context: 'pricing-fetch-start' },
        productType,
        pricingCountry,
        pricingVariantsIds,
      }
    )

    const pricingProductType = getPricingProductType(productType)
    const initialProductType = initialPricingData.productType
      ? getPricingProductType(initialPricingData.productType)
      : null
    const initialDataMatchesProductType =
      initialProductType === pricingProductType

    const pricingItemsPromise =
      isFirstInit.current && initialDataMatchesProductType
        ? Promise.resolve(initialPricingData)
        : api.call(
            getPricingItems({
              productType: getDiscountProductTypes(productType),
              pricingCountry,
              variantsIds: pricingVariantsIds,
            })
          )
    isFirstInit.current = false
    const discountsPromise = fetchDiscounts()
    const [pricingItemRes, discountsRes] = await Promise.all([
      pricingItemsPromise,
      discountsPromise,
    ])
    const { pricingItems, currency } = pricingItemRes

    logger.info('[PricingProvider] - Pricing fetch completed', {
      tags: { context: 'pricing-fetch-complete' },
      pricingItemsReceived: pricingItems?.length || 0,
      currency,
      hasDiscounts: !!discountsRes?.discounts,
    })

    return {
      pricingItems,
      currency,
      discounts: discountsRes.discounts,
      pricingProductType,
    }
  }

  const updatePricingFromAPI = debounce(async () => {
    setIsLoading(true)
    setError(null)
    try {
      const { pricingItems, currency, discounts, pricingProductType } =
        await runAsyncWithRetries<FetchPricingResponse>({
          op: fetchPricing,
          opName: FETCH_PRICING_ITEMS_OPERATION_NAME,
          numRetries: REFRESH_PRICING_NUM_RETRIES,
        })
      setDataForAll(pricingItems, discounts, currency, pricingProductType)
      currencyState.setCurrency(currency)
      promoCodeManager.revalidateDiscountCouponsForCurrency(currency)
    } catch (e: any) {
      handleError('Failed to reload price for user', e, 'reload_price')
    }
  }, DEBOUNCE_INTERVAL)

  useEffect(() => {
    if (shouldUseAPI(productType)) {
      updatePricingFromAPI()
    } else {
      isFirstInit.current = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingVariantsIds, productType])

  const handlePromocodeChange = useEvent(
    async (params?: { freeTiles?: boolean; giftCard?: true }) => {
      const { freeTiles, giftCard } = params || {}
      const curPromocode = promoCodeManager.getDiscountCouponDisplayName()
      // Free tiles is the only exception because it doesn't get loaded into the local storage like other promocodes
      if (
        !shouldUseAPI(productType) ||
        (!freeTiles && !giftCard && lastPromocode.current === curPromocode)
      ) {
        return
      }
      setDiscountsIsLoading(true)
      try {
        const { discounts: value } =
          await runAsyncWithRetries<FetchDiscountsResponse>({
            op: fetchDiscounts,
            opName: RELOAD_DISCOUNTS_OPERATION_NAME,
            numRetries: REFRESH_PRICING_NUM_RETRIES,
          })
        setDiscounts({
          value,
          isLoading: false,
        })
        lastPromocode.current = curPromocode
      } catch (e: any) {
        handleError(
          'failed to fetch discount after promocode update',
          e,
          'fetch_discount'
        )
      }
    }
  )

  const setupSubscriptions = () => {
    const promocodeSubscription = promoCodeManager.promoCodeSubject.subscribe(
      handlePromocodeChange
    )

    const experimentSubscription = experimentManager.subscribeToExperimentFetch(
      () => {
        setPricingVariantsIds(getRelevantVariants(experimentManager))
      }
    )

    return () => {
      promocodeSubscription && promocodeSubscription.unsubscribe()
      experimentSubscription && experimentSubscription.unsubscribe()
    }
  }

  useOnMount(() => {
    setupSubscriptions()
    promoCodeManager.revalidateGiftCardCodes()
  })

  const fetchPricingItem = (
    pricingProduct: PricingProduct,
    source: string
  ): FetchPricingItemResponse => {
    if (
      !pricingProduct ||
      !pricingData ||
      pricingData.isLoading ||
      pricingData.productType !== getPricingProductType(productType) ||
      pricingProduct.tileSize.includes(MIXED_SIZES) ||
      pricingProduct.materialType?.includes(MIXED_MATERIALS)
    ) {
      return { value: null, isLoading: true, currency: null }
    }

    const pricingItem = fallbackUntilMatch(
      pricingProduct,
      (pricingProduct: PricingProduct) =>
        pricingData.pricingItems[hashTilePricingProduct(pricingProduct)]
    )

    if (!pricingItem) {
      if (
        getOrderedSizes(
          pricingProduct.materialType as MATERIAL_TYPES,
          experimentManager
        ).includes(pricingProduct.tileSize as TILE_SIZES) &&
        !pricingProduct.frameColor
      ) {
        // We report error if the given SKU should be in the pricing but it's not
        const message = `Failed to find pricing item for tileSKU ${JSON.stringify(
          pricingProduct
        )}, pricingData.productType: ${
          pricingData.productType
        }, source: ${source}, all tileSkus: ${JSON.stringify(
          pricingData.pricingItems
        )}`
        const couponName = promoCodeManager.getDiscountCouponDisplayName()
        reportPricingError(
          message,
          new Error(message),
          'missing_sku',
          couponName
        )
      }
      return { value: null, isLoading: true, currency: null }
    }
    return {
      value: pricingItem.price,
      currency: pricingData.currency,
      isLoading: false,
    }
  }

  const calculateBatchPricing = useCallback(
    async (
      productsCollections: ProductCollection[],
      _productType?: PRODUCT_TYPES,
      forcePromoCode?: string
    ): Promise<BatchProductsPricingResponse> => {
      return api.call(
        calculateBatchProductsPricing({
          productsCollections,
          productType: _productType || productType,
          pricingCountry,
          variantsIds: pricingVariantsIds,
          promocode: forcePromoCode || lastPromocode.current,
        })
      )
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [productType, pricingCountry, pricingVariantsIds, lastPromocode.current]
  )

  const fetchPrices = async () => {
    const currPriceDiff: Partial<
      Record<MATERIAL_TYPES, Partial<Record<FRAME_COLORS, number>>>
    > = {}

    const { value: blackValue, isLoading: blackIsLoading } =
      await fetchPricingItem(
        {
          tileSize: TILE_SIZES.SQUARE_8X8,
          materialType: MATERIAL_TYPES.CLASSIC,
          frameColor: FRAME_COLORS.BLACK,
        },
        'fetchPrices/black_reference'
      )
    if (blackIsLoading || blackValue === 0) {
      return
    }
    for (const materialType of materialTypes) {
      currPriceDiff[materialType] = {}

      for (const frameColor of frameColors) {
        const { value: currentValue, isLoading: currentIsLoading } =
          fetchPricingItem(
            {
              tileSize: TILE_SIZES.SQUARE_8X8,
              materialType,
              frameColor,
            },
            'fetchPrices/color_loop'
          )

        if (currentIsLoading) {
          // If the material is missing or still loading, set the difference to 0
          currPriceDiff[materialType]![frameColor] = 0
        } else {
          currPriceDiff[materialType]![frameColor] = currentValue - blackValue
        }
      }
    }

    setPriceDifferenceMap(currPriceDiff as PriceDifferenceMap)
  }

  useEffect(() => {
    fetchPrices()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pricingData])

  const mixColorPrice = useMemo(() => {
    if (!priceDifferenceMap) {
      return null
    }

    return formatPrice(
      priceDifferenceMap[MATERIAL_TYPES.CLASSIC][FRAME_COLORS.BLACK],
      pricingData.currency,
      false,
      false
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [priceDifferenceMap])

  const contextValue: PricingContextValue = {
    pricingData,
    discounts,
    error,
    pricingVariantsIds,
    fetchDiscount,
    fetchDiscounts,
    fetchDiscountsForPromoCode,
    fetchPricingItem,
    calculateBatchPricing,
    priceDifferenceMap,
    mixColorPrice,
  }

  return (
    <PricingContext.Provider value={contextValue}>
      {children}
    </PricingContext.Provider>
  )
}
